import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Clock,
  Send,
  Globe2,
  ExternalLink,
  CheckCircle2,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSeo from '../hooks/useSeo';
import {
  getPhonePlaceholder,
  sanitizePhoneInput,
} from '../utils/phone';
import {
  compose,
  email as emailRule,
  errorsFromApi,
  formatError,
  maxLength,
  minLength,
  phone as phoneRule,
  required,
  validateForm,
} from '../utils/formValidation';

const SCHEMA = {
  full_name: compose(required, minLength(2), maxLength(200)),
  email: compose(required, emailRule),
  phone: compose(required, phoneRule),
  message: compose(required, minLength(10), maxLength(5000)),
  subject: maxLength(200),
};

const initialForm = () => ({
  full_name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  service_interest: '',
});

const API_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const DEFAULT_CLINIC = {
  clinic_name: 'Alanya Crystal Dental',
  clinic_address: 'Cikcili Mah. Azakoglu Cad. No:35 B Alanya / Antalya',
  clinic_phone: '+90 551 282 42 42',
  clinic_whatsapp: '+90 551 282 42 42',
  clinic_email: 'info@alncrystaldental.com',
  google_maps_url: '',
};

const sanitizeTel = (v) => String(v || '').replace(/[^\d+]/g, '');
const sanitizeWa = (v) => String(v || '').replace(/[^\d]/g, '');

const buildMapEmbed = (clinic) => {
  if (clinic?.google_maps_embed_url) return clinic.google_maps_embed_url;
  const q = encodeURIComponent(
    clinic?.clinic_address || `${clinic?.clinic_name || ''} Alanya`
  );
  return `https://www.google.com/maps?q=${q}&output=embed`;
};

const buildMapLink = (clinic) => {
  if (clinic?.google_maps_url) return clinic.google_maps_url;
  const q = encodeURIComponent(
    clinic?.clinic_address || `${clinic?.clinic_name || ''} Alanya`
  );
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

export default function Contact() {
  const { t, currentLanguage } = useLanguage();
  useSeo('contact', currentLanguage);

  const [clinic, setClinic] = useState(DEFAULT_CLINIC);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [banner, setBanner] = useState(null);
  const [done, setDone] = useState(false);

  const tErr = (key) => formatError(key, t);

  const runField = (name, value) => {
    const validator = SCHEMA[name];
    if (!validator) return;
    const err = validator(value);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  // Pull clinic settings (address, phone, etc.)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/public?group=contact`);
        if (!res.ok) return;
        const payload = await res.json();
        if (!cancelled && payload?.data) {
          setClinic((prev) => ({ ...prev, ...payload.data }));
        }
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const phoneNumber = useMemo(() => sanitizeTel(clinic?.clinic_phone), [clinic]);
  const waNumber = useMemo(() => sanitizeWa(clinic?.clinic_whatsapp), [clinic]);
  const phonePlaceholder = useMemo(
    () => getPhonePlaceholder(currentLanguage),
    [currentLanguage]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) runField(name, value);
  };

  const handlePhoneChange = (e) => {
    const value = sanitizePhoneInput(e.target.value);
    setForm((prev) => ({ ...prev, phone: value }));
    if (touched.phone) runField('phone', value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    runField(name, value);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBanner(null);

    const found = validateForm(form, SCHEMA);
    if (Object.keys(found).length) {
      setErrors(found);
      setTouched(
        Object.keys(SCHEMA).reduce((acc, k) => ({ ...acc, [k]: true }), {})
      );
      setBanner({
        type: 'error',
        msg: t('validation.fixErrors', 'Please fix the highlighted fields and try again.'),
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          preferred_language: currentLanguage,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const apiErrs = errorsFromApi(data);
        if (apiErrs) {
          setErrors(apiErrs);
          setTouched(
            Object.keys(apiErrs).reduce((acc, k) => ({ ...acc, [k]: true }), {})
          );
          setBanner({
            type: 'error',
            msg: t('validation.fixErrors', 'Please fix the highlighted fields and try again.'),
          });
        } else if (res.status === 429) {
          setBanner({
            type: 'error',
            msg: t('validation.rateLimit', 'Too many requests — please try again later.'),
          });
        } else {
          setBanner({
            type: 'error',
            msg:
              data?.message ||
              t('validation.serverError', 'Something went wrong on our side. Please try again.'),
          });
        }
        return;
      }

      toast.success(t('contact.form.success', "Thank you! We'll contact you shortly."));
      setForm(initialForm());
      setErrors({});
      setTouched({});
      setBanner(null);
      setDone(true);
    } catch {
      setBanner({
        type: 'error',
        msg: t('validation.serverError', 'Something went wrong on our side. Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const methods = [
    {
      key: 'call',
      icon: Phone,
      iconClass: 'icon-blue',
      title: t('contactPage.methods.call.title', 'Call us'),
      desc: t('contactPage.methods.call.desc', 'Talk to our patient coordinator directly.'),
      cta: t('contactPage.methods.call.cta', 'Call now'),
      value: clinic?.clinic_phone,
      href: phoneNumber ? `tel:${phoneNumber}` : null,
      external: false,
    },
    {
      key: 'whatsapp',
      icon: MessageCircle,
      iconClass: 'icon-green',
      title: t('contactPage.methods.whatsapp.title', 'WhatsApp'),
      desc: t(
        'contactPage.methods.whatsapp.desc',
        'Send photos of your teeth for a free pre-assessment.'
      ),
      cta: t('contactPage.methods.whatsapp.cta', 'Open WhatsApp'),
      value: clinic?.clinic_whatsapp,
      href: waNumber ? `https://wa.me/${waNumber}` : null,
      external: true,
    },
    {
      key: 'email',
      icon: Mail,
      iconClass: 'icon-amber',
      title: t('contactPage.methods.email.title', 'Email'),
      desc: t(
        'contactPage.methods.email.desc',
        'Detailed treatment quotes within 24 hours.'
      ),
      cta: t('contactPage.methods.email.cta', 'Send email'),
      value: clinic?.clinic_email,
      href: clinic?.clinic_email ? `mailto:${clinic.clinic_email}` : null,
      external: false,
    },
    {
      key: 'visit',
      icon: MapPin,
      iconClass: 'icon-purple',
      title: t('contactPage.methods.visit.title', 'Visit the clinic'),
      desc: t(
        'contactPage.methods.visit.desc',
        'Walk-ins welcome during opening hours.'
      ),
      cta: t('contactPage.methods.visit.cta', 'Get directions'),
      value: clinic?.clinic_address,
      href: buildMapLink(clinic),
      external: true,
    },
  ];

  const hours = [
    {
      label: t('contactPage.hours.weekdays', 'Monday – Friday'),
      value: t('contactPage.hours.weekdaysHours', '09:00 – 19:00'),
      open: true,
    },
    {
      label: t('contactPage.hours.saturday', 'Saturday'),
      value: t('contactPage.hours.saturdayHours', '09:00 – 17:00'),
      open: true,
    },
    {
      label: t('contactPage.hours.sunday', 'Sunday'),
      value: t('contactPage.hours.closed', 'Closed'),
      open: false,
    },
  ];

  return (
    <div className="contact-page">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="contact-hero">
        <div className="container-premium">
          <div className="contact-hero-inner">
            <span className="section-eyebrow light">
              {t('contactPage.hero.eyebrow', 'Contact')}
            </span>
            <h1 className="contact-hero-title">
              {t('contactPage.hero.title', "We'd love to hear from you")}
            </h1>
            <p className="contact-hero-sub">
              {t(
                'contactPage.hero.subtitle',
                "Treatment plans, second opinions or just a quick question — our coordinators reply within minutes during opening hours."
              )}
            </p>

            <div className="contact-hero-badges">
              <span className="contact-hero-badge">
                <ShieldCheck size={14} /> {t('trustStrip.iso', 'ISO 9001')}
              </span>
              <span className="contact-hero-badge">
                <CheckCircle2 size={14} /> {t('hero.online', 'Online now — replies in ~5 min')}
              </span>
              <span className="contact-hero-badge">
                <Globe2 size={14} /> EN · TR · RU · NO · DE
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact methods ──────────────────────────────── */}
      <section className="section-padding section-tone-b">
        <div className="container-premium">
          <div className="section-head">
            <span className="section-eyebrow">
              {t('contactPage.methods.title', 'Pick the channel that suits you')}
            </span>
            <h2 className="section-title">
              {t(
                'contactPage.methods.subtitle',
                'Free consultations · No obligation · Replies in your language'
              )}
            </h2>
          </div>

          <div className="contact-methods-grid">
            {methods.map((m) => {
              const Icon = m.icon;
              const Tag = m.href ? 'a' : 'div';
              const linkProps = m.href
                ? m.external
                  ? { href: m.href, target: '_blank', rel: 'noopener noreferrer' }
                  : { href: m.href }
                : {};
              return (
                <Tag
                  key={m.key}
                  className={`contact-method-card ${m.href ? 'is-clickable' : ''}`}
                  {...linkProps}
                >
                  <div className={`contact-method-icon ${m.iconClass}`}>
                    <Icon size={22} />
                  </div>
                  <h3 className="contact-method-title">{m.title}</h3>
                  <p className="contact-method-desc">{m.desc}</p>
                  {m.value ? (
                    <div className="contact-method-value">{m.value}</div>
                  ) : null}
                  {m.href ? (
                    <span className="contact-method-cta">
                      {m.cta}
                      {m.external ? <ExternalLink size={13} /> : null}
                    </span>
                  ) : null}
                </Tag>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Form + side info ─────────────────────────────── */}
      <section className="section-padding section-tone-a">
        <div className="container-premium">
          <div className="contact-form-grid">
            {done ? (
              <div className="contact-form-card">
                <div className="appointment-success">
                  <div className="appointment-success-icon">
                    <CheckCircle2 size={32} />
                  </div>
                  <h3>
                    {t(
                      'contact.form.success',
                      "Thank you! We'll contact you shortly."
                    )}
                  </h3>
                  <p style={{ color: 'rgba(11,79,108,0.72)', maxWidth: 460 }}>
                    {t(
                      'contact.form.successDetail',
                      'Your message is on its way to our coordinators. Most replies arrive within a few minutes during opening hours.'
                    )}
                  </p>
                  <div className="appointment-success-cta">
                    {waNumber ? (
                      <a
                        href={`https://wa.me/${waNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-crystal"
                      >
                        <MessageCircle size={16} />
                        {t('appointmentPage.side.whatsapp', 'WhatsApp us')}
                      </a>
                    ) : null}
                    <button
                      type="button"
                      className="btn-crystal-outline"
                      onClick={() => setDone(false)}
                    >
                      {t('contact.form.sendAnother', 'Send another message')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
            <form className="contact-form-card" onSubmit={submit} noValidate>
              <div className="contact-form-head">
                <h2 className="contact-form-title">
                  {t('contactPage.form.title', 'Send us a message')}
                </h2>
                <p className="contact-form-sub">
                  {t(
                    'contactPage.form.subtitle',
                    "Tell us a bit about what you're looking for — we'll come back with a personalised plan."
                  )}
                </p>
              </div>

              {banner && (
                <div className={`form-banner ${banner.type}`} role="alert">
                  <AlertCircle size={18} />
                  <span>{banner.msg}</span>
                </div>
              )}

              <div className="contact-form-fields">
                <div className="contact-form-row two">
                  <div className={`field ${errors.full_name ? 'has-error' : ''}`}>
                    <label htmlFor="cp-full_name" className="field-label">
                      {t('contact.form.name', 'Full name')} <span className="req">*</span>
                    </label>
                    <input
                      id="cp-full_name"
                      name="full_name"
                      className="field-input"
                      value={form.full_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={Boolean(errors.full_name)}
                      aria-describedby={errors.full_name ? 'cp-full_name-err' : undefined}
                      placeholder={t('contact.form.name', 'Full name')}
                    />
                    {errors.full_name && (
                      <span id="cp-full_name-err" className="field-error">
                        <AlertCircle size={13} /> {tErr(errors.full_name)}
                      </span>
                    )}
                  </div>
                  <div className={`field ${errors.phone ? 'has-error' : ''}`}>
                    <label htmlFor="cp-phone" className="field-label">
                      {t('contact.form.phone', 'Phone number')} <span className="req">*</span>
                    </label>
                    <input
                      id="cp-phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      className="field-input"
                      value={form.phone}
                      onChange={handlePhoneChange}
                      onBlur={handleBlur}
                      aria-invalid={Boolean(errors.phone)}
                      aria-describedby={errors.phone ? 'cp-phone-err' : undefined}
                      placeholder={phonePlaceholder}
                    />
                    {errors.phone && (
                      <span id="cp-phone-err" className="field-error">
                        <AlertCircle size={13} /> {tErr(errors.phone)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="contact-form-row two">
                  <div className={`field ${errors.email ? 'has-error' : ''}`}>
                    <label htmlFor="cp-email" className="field-label">
                      {t('contact.form.email', 'Email address')} <span className="req">*</span>
                    </label>
                    <input
                      id="cp-email"
                      name="email"
                      type="email"
                      className="field-input"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? 'cp-email-err' : undefined}
                      placeholder="you@example.com"
                    />
                    {errors.email && (
                      <span id="cp-email-err" className="field-error">
                        <AlertCircle size={13} /> {tErr(errors.email)}
                      </span>
                    )}
                  </div>
                  <div className={`field ${errors.subject ? 'has-error' : ''}`}>
                    <label htmlFor="cp-subject" className="field-label">
                      {t('contact.form.subject', 'Treatment / Subject')}
                    </label>
                    <input
                      id="cp-subject"
                      name="subject"
                      className="field-input"
                      value={form.subject}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={Boolean(errors.subject)}
                      aria-describedby={errors.subject ? 'cp-subject-err' : undefined}
                      placeholder={t('contact.form.subject', 'Treatment / Subject')}
                    />
                    {errors.subject && (
                      <span id="cp-subject-err" className="field-error">
                        <AlertCircle size={13} /> {tErr(errors.subject)}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`field ${errors.message ? 'has-error' : ''}`}>
                  <label htmlFor="cp-message" className="field-label">
                    {t('contact.form.message', 'Your message')} <span className="req">*</span>
                  </label>
                  <textarea
                    id="cp-message"
                    name="message"
                    className="field-textarea"
                    value={form.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={5}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'cp-message-err' : undefined}
                    placeholder={t('contact.form.message', 'Your message')}
                  />
                  {errors.message && (
                    <span id="cp-message-err" className="field-error">
                      <AlertCircle size={13} /> {tErr(errors.message)}
                    </span>
                  )}
                </div>
              </div>

              <div className="contact-form-actions">
                <button
                  type="submit"
                  className="btn-crystal full"
                  disabled={submitting}
                >
                  {submitting ? (
                    t('contact.form.sending', 'Sending...')
                  ) : (
                    <>
                      <Send size={16} /> {t('contact.form.submit', 'Send Message')}
                    </>
                  )}
                </button>
              </div>
            </form>
            )}

            <aside className="contact-side">
              <div className="contact-side-card">
                <div className="contact-side-head">
                  <Clock size={18} />
                  <h3>{t('contactPage.hours.title', 'Opening hours')}</h3>
                </div>
                <ul className="contact-hours">
                  {hours.map((h, idx) => (
                    <li key={idx} className={!h.open ? 'is-closed' : ''}>
                      <span>{h.label}</span>
                      <strong>{h.value}</strong>
                    </li>
                  ))}
                </ul>
                <p className="contact-side-note">
                  {t(
                    'contactPage.hours.note',
                    'Emergency support available 24/7 for active patients via WhatsApp.'
                  )}
                </p>
              </div>

              <div className="contact-side-card">
                <div className="contact-side-head">
                  <Globe2 size={18} />
                  <h3>{t('contactPage.languages.title', 'We speak your language')}</h3>
                </div>
                <p className="contact-side-note">
                  {t(
                    'contactPage.languages.subtitle',
                    'Coordinators on staff for English, Turkish, Russian, Norwegian and German. Medical translators available on request.'
                  )}
                </p>
                <div className="contact-lang-row">
                  <span className="contact-lang-chip">EN</span>
                  <span className="contact-lang-chip">TR</span>
                  <span className="contact-lang-chip">RU</span>
                  <span className="contact-lang-chip">NO</span>
                  <span className="contact-lang-chip">DE</span>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ── Map ──────────────────────────────────────────── */}
      <section className="section-padding section-tone-b" style={{ paddingTop: 0 }}>
        <div className="container-premium">
          <div className="section-head">
            <span className="section-eyebrow">
              {t('contactPage.map.title', 'Find us in Alanya')}
            </span>
            <h2 className="section-title">
              {t(
                'contactPage.map.subtitle',
                '5 minutes from the city centre · Free parking on site'
              )}
            </h2>
          </div>
          <div className="contact-map-wrap">
            <iframe
              title="Crystal Dental Location"
              src={buildMapEmbed(clinic)}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <a
              className="contact-map-cta"
              href={buildMapLink(clinic)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={14} />
              {t('contactPage.map.open', 'Open in Google Maps')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
